import type { TicketResource } from '@repo/api-client';

/**
 * What a ticket was sold against, and why this is not the catalogue's list of
 * kinds.
 *
 * A catalogue item is one of three kinds and a **ticket carries only two of
 * them**. A `PRODUCT` is sold through Sales and cannot reach the tickets table
 * at all — the API says so twice, in `tickets_item_kind_issuable` as a SQL
 * CHECK and in `ListTicketsQuery`'s own `TICKET_ITEM_KINDS` — so reusing
 * `catalogue/catalogue-kinds.ts`'s `CATALOGUE_KIND_ITEMS` here would offer a
 * filter whose only possible answer is an empty list. It typechecks perfectly
 * and reads as a bug in the day's takings.
 *
 * Nothing below restates the union: it is read off `TicketResource`, so a third
 * issuable kind added to the document fails to compile in this file rather than
 * rendering as `SOMETHING_NEW` on a manager's screen. `catalogue-kinds.ts` does
 * the same for the three-kind union beside it, and the two are deliberately not
 * merged — they are different sets and the whole point of this file is that
 * they are.
 */
export type TicketItemKind = TicketResource['itemKind'];

/** The kinds, in the order they are offered. */
export const TICKET_ITEM_KINDS: readonly TicketItemKind[] = ['TICKET_TYPE', 'ACTIVITY'];

/**
 * `Record` rather than a function with a default, so exhaustiveness is the type
 * system's problem and not a runtime fallback nobody ever sees.
 *
 * The words match the catalogue screen's, because they name the same thing to
 * the same people — a manager who filtered the catalogue by "Ticket type" this
 * morning must not meet "Gate ticket" here this afternoon.
 */
const KIND_LABELS: Record<TicketItemKind, string> = {
  TICKET_TYPE: 'Ticket type',
  ACTIVITY: 'Activity',
};

export const labelForItemKind = (kind: TicketItemKind): string => KIND_LABELS[kind];

/**
 * The same kinds as a value-to-label map, for `<Select items={…}>`.
 *
 * Base UI resolves what the trigger shows from this prop and not from the
 * rendered `<SelectItem>`s, so a select given options but no `items` shows the
 * raw value — `TICKET_TYPE` rather than "Ticket type". Derived from
 * `TICKET_ITEM_KINDS` so the two cannot drift.
 */
export const TICKET_ITEM_KIND_ITEMS: ReadonlyArray<{ label: string; value: TicketItemKind }> =
  TICKET_ITEM_KINDS.map((kind) => ({ label: labelForItemKind(kind), value: kind }));
